import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "common";
import { colors } from "../components/Theme/WebTheme";
import { FONT_FAMILY, MAIN_COLOR, SECONDORY_COLOR, labelText } from "../common/sharedFunctions";
import { normalizeUserTypePermissions } from "../common/userPermissions";
import { canDoAction } from "common/src/actions/userPermissions";

const ACTIONS = ["view", "add", "create", "edit", "delete"];

const ROLE_DEFINITIONS = [
  {
    id: "customer",
    label: "Customer",
    description: "Creates bookings and manages own shipments.",
    system: true,
    active: true
  },
  {
    id: "driver",
    label: "Driver",
    description: "Receives assigned bookings, updates trip status, uploads proof.",
    system: true,
    active: true
  },
  {
    id: "fleetadmin",
    label: "Fleet Admin / Logistics Company",
    description: "Manages drivers, vehicles, bids, bookings, wallet, and reports.",
    system: true,
    active: true
  },
  {
    id: "admin",
    label: "Admin / Super Admin",
    description: "Full access to system settings, users, bookings, and reports.",
    system: true,
    active: true
  }
];

const MODULE_DEFINITIONS = [
  { id: "dashboard", label: "Dashboard", route: "/dashboard" },
  { id: "bookingHistory", label: "Booking History", route: "/bookings" },
  { id: "bookingOrdersForBid", label: "Booking Orders For Bid", route: "/booking-orders-for-bid" },
  { id: "adminBidMonitoring", label: "Admin Bid Monitoring", route: "/admin-bid-monitoring" },
  { id: "emptyContainerReturn", label: "Empty Container Return", route: "/empty-container-returns" },
  { id: "addBookings", label: "Add Bookings", route: "/addbookings" },
  { id: "users", label: "Users", route: "/users/0" },
  { id: "vehicleType", label: "Vehicle Type", route: "/cartypes" },
  { id: "containerYard", label: "Container Yard", route: "/containeryards" },
  { id: "additionalFeesTitle", label: "Additional Fees Title", route: "/additionalfees" },
  { id: "chatProof", label: "Chat Proof", route: "/adminchats" },
  { id: "dataImportExport", label: "Data Import Export", route: "/data-import-export" },
  { id: "carsAndDrivers", label: "Cars & Drivers", route: "/cars" },
  { id: "withdraws", label: "Withdraws", route: "/withdraws" },
  { id: "addToWallet", label: "Add to Wallet", route: "/addtowallet" },
  { id: "reports", label: "Reports", route: "/allreports" },
  { id: "promos", label: "Promos", route: "/promos" },
  { id: "pushNotifications", label: "Push Notifications", route: "/notifications" },
  { id: "ec", label: "ECall", route: "/sos" },
  { id: "complaint", label: "Complaint", route: "/complain" },
  { id: "settings", label: "Settings", route: "/settings" },
  { id: "workflowConfiguration", label: "Workflow Configuration", route: "/workflow-configuration" },
  { id: "paymentSettings", label: "Payment Settings", route: "/paymentsettings" },
  { id: "myWallet", label: "My Wallet", route: "/userwallet" },
  { id: "profile", label: "Profile", route: "/profile" }
];

const DEFAULT_ROLE_ACTIONS = {
  customer: {
    bookingHistory: ["view", "edit"],
    addBookings: ["view", "add", "create"],
    myWallet: ["view"],
    profile: ["view", "edit"]
  },
  driver: {
    bookingHistory: ["view", "edit"],
    emptyContainerReturn: ["view", "add", "create", "edit"],
    carsAndDrivers: ["view", "edit"],
    myWallet: ["view"],
    profile: ["view", "edit"]
  },
  fleetadmin: {
    dashboard: ["view"],
    bookingHistory: ["view", "edit"],
    bookingOrdersForBid: ["view", "add", "create", "edit", "delete"],
    emptyContainerReturn: ["view", "edit"],
    addBookings: ["view", "add", "create"],
    users: ["view", "add", "create", "edit", "delete"],
    carsAndDrivers: ["view", "add", "create", "edit", "delete"],
    reports: ["view"],
    myWallet: ["view"],
    profile: ["view", "edit"]
  }
};

const buildDefaultPermissions = () => {
  return ROLE_DEFINITIONS.reduce((roleMap, role) => {
    roleMap[role.id] = MODULE_DEFINITIONS.reduce((moduleMap, module) => {
      const allowedActions = DEFAULT_ROLE_ACTIONS[role.id]?.[module.id] || [];
      moduleMap[module.id] = ACTIONS.reduce((actionMap, action) => {
        actionMap[action] = role.id === "admin" || allowedActions.includes(action);
        return actionMap;
      }, {});
      return moduleMap;
    }, {});
    return roleMap;
  }, {});
};

const mergeSavedPermissions = (settings = {}) => {
  const defaults = buildDefaultPermissions();
  const saved = settings.accessPermissions?.permissions || {};
  Object.keys(saved).forEach((roleId) => {
    if (!defaults[roleId]) defaults[roleId] = {};
    Object.keys(saved[roleId] || {}).forEach((moduleId) => {
      defaults[roleId][moduleId] = {
        ...(defaults[roleId][moduleId] || {}),
        ...(saved[roleId][moduleId] || {})
      };
    });
  });
  if (defaults.admin) {
    Object.keys(defaults.admin).forEach((moduleId) => {
      ACTIONS.forEach((action) => {
        defaults.admin[moduleId][action] = true;
      });
    });
  }
  return defaults;
};

const buildRolesMap = () => {
  return ROLE_DEFINITIONS.reduce((map, role) => {
    map[role.id] = {
      label: role.label,
      system: role.system,
      active: role.active,
      planned: !!role.planned
    };
    return map;
  }, {});
};

const buildModulesMap = () => {
  return MODULE_DEFINITIONS.reduce((map, module) => {
    map[module.id] = {
      label: module.label,
      route: module.route,
      sidebar: true
    };
    return map;
  }, {});
};

const buildLegacyUserTypePermissions = (settings = {}) => {
  if (settings.userTypePermissions) return settings.userTypePermissions;
  return normalizeUserTypePermissions(settings).reduce((map, row) => {
    map[row.id] = {
      add: !!row.add,
      edit: !!row.edit,
      view: !!row.view,
      delete: !!row.delete
    };
    return map;
  }, {});
};

const buildAccessPermissionsPayload = (settings = {}, permissions = {}) => {
  const existing = settings.accessPermissions || {};
  return {
    ...existing,
    roles: {
      ...(existing.roles || {}),
      ...buildRolesMap()
    },
    modules: {
      ...(existing.modules || {}),
      ...buildModulesMap()
    },
    permissions: {
      ...(existing.permissions || {}),
      ...permissions
    }
  };
};

const getUserName = (user = {}) => {
  const fullName = `${user.firstName || ""} ${user.lastName || ""}`.trim();
  return user.companyName || user.businessName || fullName || user.name || user.email || "-";
};

const MATRIX_ROLE_DEFINITIONS = [
  ...ROLE_DEFINITIONS.filter((role) => role.id !== "admin"),
  ROLE_DEFINITIONS.find((role) => role.id === "admin")
].filter(Boolean);

const getRoleLabel = (roleId) => {
  return ROLE_DEFINITIONS.find((role) => role.id === roleId)?.label || roleId || "-";
};

const getActiveStatus = (user = {}) => {
  if (user.usertype === "driver") return user.driverActiveStatus === false ? "Inactive" : "Active";
  if (user.active === false) return "Inactive";
  return "Active";
};

const getApprovalStatus = (user = {}) => {
  if (user.approved === false || user.approved === "false") return "Pending";
  return "Approved";
};

const getUserId = (user = {}) => user.id || user.uid || user.key || "";

const getUserRole = (user = {}) => user.usertype || user.userType || "unknown";

const getFleetAdminName = (user = {}, users = []) => {
  if (!user.fleetadmin) return "";
  const fleetAdmin = users.find((item) => getUserId(item) === user.fleetadmin || item.uid === user.fleetadmin || item.id === user.fleetadmin);
  return fleetAdmin ? getUserName(fleetAdmin) : user.fleetadmin;
};

const getRelationshipAccessType = (user = {}, users = [], fleetStats = {}) => {
  const role = getUserRole(user);
  if (role === "driver") {
    return user.fleetadmin ? `Fleet-linked Driver${getFleetAdminName(user, users) ? " - " + getFleetAdminName(user, users) : ""}` : "Independent Driver";
  }
  if (role === "fleetadmin") {
    const id = getUserId(user);
    const stats = fleetStats[id] || { drivers: 0, vehicles: null };
    return `${stats.drivers} driver${stats.drivers === 1 ? "" : "s"} linked${stats.vehicles === null ? "" : ", " + stats.vehicles + " vehicle" + (stats.vehicles === 1 ? "" : "s")}`;
  }
  if (role === "customer") return "Customer account";
  if (role === "admin") return "System administration";
  return "-";
};

const getEditRouteForUser = (user = {}) => {
  const id = getUserId(user);
  if (!id) return "";
  const role = getUserRole(user);
  if (role === "customer") return `/users/edituser/customer/${id}`;
  if (role === "driver") return `/users/edituser/driver/${id}`;
  if (role === "fleetadmin") return `/users/edituser/fleetadmin/${id}`;
  if (role === "admin") return `/users/edituser/admin/${id}`;
  return "";
};

export default function UserPermissions() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const settings = useSelector((state) => state.settingsdata.settings || {});
  const users = useSelector((state) => state.usersdata.staticusers || []);
  const auth = useSelector((state) => state.auth || {});
  const { editSettings, fetchUsersOnce } = api;
  const [permissions, setPermissions] = useState(buildDefaultPermissions());
  const [selectedRole, setSelectedRole] = useState("customer");
  const [userRoleFilter, setUserRoleFilter] = useState("all");
  const [userSearch, setUserSearch] = useState("");
  const [approvalFilter, setApprovalFilter] = useState("all");
  const [activeFilter, setActiveFilter] = useState("all");
  const [driverTypeFilter, setDriverTypeFilter] = useState("all");
  const [saveMessage, setSaveMessage] = useState("");

  const label = (key, fallback) => labelText(t, key, fallback);

  useEffect(() => {
    dispatch(fetchUsersOnce());
  }, [dispatch, fetchUsersOnce]);

  useEffect(() => {
    setPermissions(mergeSavedPermissions(settings));
  }, [settings]);

  const userCounts = useMemo(() => {
    return users.reduce((map, user) => {
      const roleId = user.usertype || "unknown";
      map[roleId] = (map[roleId] || 0) + 1;
      return map;
    }, {});
  }, [users]);

  const currentUserRole = auth.profile?.usertype || auth.profile?.userType || auth.user?.usertype || "admin";

  const canViewUsersModule = canDoAction({
    role: currentUserRole,
    moduleKey: "users",
    action: "view",
    accessPermissions: settings.accessPermissions,
    fallback: true
  });

  const canEditUsersModule = canDoAction({
    role: currentUserRole,
    moduleKey: "users",
    action: "edit",
    accessPermissions: settings.accessPermissions,
    fallback: true
  });

  const fleetStats = useMemo(() => {
    return users.reduce((map, user) => {
      if (getUserRole(user) === "driver" && user.fleetadmin) {
        map[user.fleetadmin] = map[user.fleetadmin] || { drivers: 0, vehicles: null };
        map[user.fleetadmin].drivers += 1;
      }
      return map;
    }, {});
  }, [users]);

  const userRoleCounts = useMemo(() => {
    return users.reduce((counts, user) => {
      const role = getUserRole(user);
      counts[role] = (counts[role] || 0) + 1;
      if (role === "driver" && user.fleetadmin) counts.fleetLinkedDrivers += 1;
      if (role === "driver" && !user.fleetadmin) counts.independentDrivers += 1;
      return counts;
    }, { customer: 0, driver: 0, fleetadmin: 0, admin: 0, independentDrivers: 0, fleetLinkedDrivers: 0 });
  }, [users]);

  const filteredUsersByRole = useMemo(() => {
    const search = userSearch.trim().toLowerCase();
    return users.filter((user) => {
      const role = getUserRole(user);
      if (userRoleFilter !== "all" && role !== userRoleFilter) return false;
      if (approvalFilter !== "all" && getApprovalStatus(user).toLowerCase() !== approvalFilter) return false;
      if (activeFilter !== "all" && getActiveStatus(user).toLowerCase() !== activeFilter) return false;
      if (role === "driver" && driverTypeFilter === "independent" && user.fleetadmin) return false;
      if (role === "driver" && driverTypeFilter === "fleet-linked" && !user.fleetadmin) return false;
      if (search) {
        const searchable = [
          getUserName(user),
          user.companyName,
          user.businessName,
          user.email,
          getRoleLabel(role),
          getRelationshipAccessType(user, users, fleetStats)
        ].filter(Boolean).join(" ").toLowerCase();
        return searchable.includes(search);
      }
      return true;
    });
  }, [users, userRoleFilter, approvalFilter, activeFilter, driverTypeFilter, userSearch, fleetStats]);

  const accessPreview = useMemo(() => {
    return MODULE_DEFINITIONS.reduce((preview, module) => {
      const canView = !!permissions[selectedRole]?.[module.id]?.view;
      preview[canView ? "can" : "cannot"].push(module.label);
      return preview;
    }, { can: [], cannot: [] });
  }, [permissions, selectedRole]);

  const setPermission = (roleId, moduleId, action, checked) => {
    if (roleId === "admin") return;
    setSaveMessage("");
    setPermissions((current) => {
      const next = {
        ...current,
        [roleId]: {
          ...(current[roleId] || {}),
          [moduleId]: {
            ...(current[roleId]?.[moduleId] || {}),
            [action]: checked
          }
        }
      };
      if (action === "add") next[roleId][moduleId].create = checked;
      if (action === "create") next[roleId][moduleId].add = checked;
      return next;
    });
  };

  const savePermissions = () => {
    dispatch(editSettings({
      ...settings,
      userTypePermissions: buildLegacyUserTypePermissions(settings),
      accessPermissions: buildAccessPermissionsPayload(settings, permissions)
    }));
    setSaveMessage(label("permissions_saved", "Permissions saved safely."));
  };

  return (
    <Box style={{ direction: isRTL === "rtl" ? "rtl" : "ltr" }}>
      <Paper style={{ borderRadius: 8, boxShadow: `0px 2px 5px ${SECONDORY_COLOR}`, padding: 20, backgroundColor: colors.WHITE }}>
        <Box style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
          <Box>
            <Typography variant="h5" style={{ fontFamily: FONT_FAMILY, fontWeight: 700 }}>
              {label("roles_permissions_users_access", "Roles, Permissions, Users & Access")}
            </Typography>
            <Typography variant="body2" style={{ fontFamily: FONT_FAMILY, color: "#5f6b7a", marginTop: 4 }}>
              {label("access_phase_two_note", "Saved permissions are enforced consistently by the sidebar, protected routes, and supported page actions.")}
            </Typography>
          </Box>
          <Button variant="contained" onClick={savePermissions} style={{ backgroundColor: MAIN_COLOR, color: colors.WHITE, fontFamily: FONT_FAMILY }}>
            {label("save_permissions", "Save Permissions")}
          </Button>
        </Box>
        {saveMessage ? <Alert severity="success" style={{ marginTop: 16, fontFamily: FONT_FAMILY }}>{saveMessage}</Alert> : null}
      </Paper>

      <Paper style={{ borderRadius: 8, boxShadow: `0px 2px 5px ${SECONDORY_COLOR}`, padding: 20, backgroundColor: colors.WHITE, marginTop: 16 }}>
        <Typography variant="h6" style={{ fontFamily: FONT_FAMILY, fontWeight: 700, marginBottom: 12 }}>1. Roles</Typography>
        <TableContainer style={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {["Role Name", "Role Code", "Description", "User Count", "Status", "Actions"].map((heading) => (
                  <TableCell key={heading} style={{ fontFamily: FONT_FAMILY, fontWeight: 700 }}>{heading}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {ROLE_DEFINITIONS.map((role) => (
                <TableRow key={role.id}>
                  <TableCell style={{ fontFamily: FONT_FAMILY, fontWeight: 700 }}>{role.label}</TableCell>
                  <TableCell style={{ fontFamily: FONT_FAMILY }}><Chip size="small" label={role.id} /></TableCell>
                  <TableCell style={{ fontFamily: FONT_FAMILY }}>{role.description}</TableCell>
                  <TableCell style={{ fontFamily: FONT_FAMILY }}>{userCounts[role.id] || 0}</TableCell>
                  <TableCell style={{ fontFamily: FONT_FAMILY }}>
                    <Chip size="small" color={role.active ? "success" : "default"} label={role.planned ? "Planned" : role.active ? "Active" : "Inactive"} />
                  </TableCell>
                  <TableCell style={{ fontFamily: FONT_FAMILY, color: "#6b7280" }}>Display only</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper style={{ borderRadius: 8, boxShadow: `0px 2px 5px ${SECONDORY_COLOR}`, padding: 20, backgroundColor: colors.WHITE, marginTop: 16 }}>
        <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
          <Typography variant="h6" style={{ fontFamily: FONT_FAMILY, fontWeight: 700 }}>2. Permission Matrix</Typography>
          <Alert severity="info" style={{ fontFamily: FONT_FAMILY }}>
            Add and Create mirror each other. Admin / Super Admin always retains full access.
          </Alert>
        </Box>
        <TableContainer style={{ overflowX: "auto" }}>
          <Table size="small" style={{ minWidth: 980 }}>
            <TableHead>
              <TableRow>
                <TableCell style={{ fontFamily: FONT_FAMILY, fontWeight: 700, minWidth: 220 }}>Module / Feature</TableCell>
                <TableCell style={{ fontFamily: FONT_FAMILY, fontWeight: 700 }}>Permission</TableCell>
                {MATRIX_ROLE_DEFINITIONS.map((role) => (
                  <TableCell align="center" key={role.id} style={{ fontFamily: FONT_FAMILY, fontWeight: 700, minWidth: 120 }}>{role.label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {MODULE_DEFINITIONS.map((module) => ACTIONS.map((action, actionIndex) => (
                <TableRow key={`${module.id}-${action}`}>
                  {actionIndex === 0 ? (
                    <TableCell rowSpan={ACTIONS.length} style={{ fontFamily: FONT_FAMILY, fontWeight: 700, borderRight: "1px solid #e5e7eb" }}>
                      {module.label}
                      <Typography variant="caption" style={{ display: "block", color: "#6b7280", fontFamily: FONT_FAMILY }}>{module.route}</Typography>
                    </TableCell>
                  ) : null}
                  <TableCell style={{ fontFamily: FONT_FAMILY, textTransform: "capitalize" }}>{action}</TableCell>
                  {MATRIX_ROLE_DEFINITIONS.map((role) => (
                    <TableCell align="center" key={role.id}>
                      <Switch
                        checked={!!permissions[role.id]?.[module.id]?.[action]}
                        disabled={role.id === "admin" || role.planned}
                        onChange={(event) => setPermission(role.id, module.id, action, event.target.checked)}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              )))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Box style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.35fr) minmax(320px, 0.65fr)", gap: 16, marginTop: 16 }}>
        <Paper style={{ borderRadius: 8, boxShadow: `0px 2px 5px ${SECONDORY_COLOR}`, padding: 20, backgroundColor: colors.WHITE }}>
          <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
            <Box>
              <Typography variant="h6" style={{ fontFamily: FONT_FAMILY, fontWeight: 700 }}>3. Users by Role</Typography>
              <Typography variant="body2" style={{ fontFamily: FONT_FAMILY, color: "#334155", marginTop: 4 }}>
                View users grouped by role and access type.
              </Typography>
              <Typography variant="body2" style={{ fontFamily: FONT_FAMILY, color: "#5f6b7a", marginTop: 4 }}>
                Role assignment is not enabled in this phase. This page only shows users by role. Role changes will require separate approval.
              </Typography>
            </Box>
            <FormControl size="small" style={{ minWidth: 260 }}>
              <Select value={userRoleFilter} onChange={(event) => setUserRoleFilter(event.target.value)}>
                <MenuItem value="all">All Users</MenuItem>
                <MenuItem value="customer">Customer</MenuItem>
                <MenuItem value="driver">Driver</MenuItem>
                <MenuItem value="fleetadmin">Fleet Admin / Logistics Company</MenuItem>
                <MenuItem value="admin">Admin / Super Admin</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10, marginBottom: 14 }}>
            {[
              ["Customers", userRoleCounts.customer],
              ["Drivers", userRoleCounts.driver],
              ["Fleet Admins / Logistics Companies", userRoleCounts.fleetadmin],
              ["Admins", userRoleCounts.admin],
              ["Independent Drivers", userRoleCounts.independentDrivers],
              ["Fleet-linked Drivers", userRoleCounts.fleetLinkedDrivers],
              ["Total Fleet Vehicles", "Not loaded"]
            ].map(([title, value]) => (
              <Box key={title} style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: 10, backgroundColor: "#f8fafc" }}>
                <Typography variant="caption" style={{ fontFamily: FONT_FAMILY, color: "#64748b" }}>{title}</Typography>
                <Typography variant="h6" style={{ fontFamily: FONT_FAMILY, fontWeight: 700 }}>{value}</Typography>
              </Box>
            ))}
          </Box>

          <Box style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10, marginBottom: 14 }}>
            <TextField size="small" label="Search name, company, email, or role" value={userSearch} onChange={(event) => setUserSearch(event.target.value)} />
            <FormControl size="small">
              <Select value={approvalFilter} onChange={(event) => setApprovalFilter(event.target.value)}>
                <MenuItem value="all">All approval statuses</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small">
              <Select value={activeFilter} onChange={(event) => setActiveFilter(event.target.value)}>
                <MenuItem value="all">All active statuses</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small">
              <Select value={driverTypeFilter} onChange={(event) => setDriverTypeFilter(event.target.value)} disabled={userRoleFilter !== "driver" && userRoleFilter !== "all"}>
                <MenuItem value="all">All driver types</MenuItem>
                <MenuItem value="independent">Independent Drivers</MenuItem>
                <MenuItem value="fleet-linked">Fleet-linked Drivers</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {!canViewUsersModule ? (
            <Alert severity="warning" style={{ fontFamily: FONT_FAMILY }}>You do not have permission to view the Users module.</Alert>
          ) : (
            <TableContainer style={{ overflowX: "auto", maxHeight: 480 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {["Name / Company Name", "Email", "Role", "Account Status", "Active Status", "Relationship / Access Type", "Actions"].map((heading) => (
                      <TableCell key={heading} style={{ fontFamily: FONT_FAMILY, fontWeight: 700 }}>{heading}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredUsersByRole.length ? filteredUsersByRole.map((user) => {
                    const editRoute = getEditRouteForUser(user);
                    return (
                      <TableRow key={getUserId(user) || user.email}>
                        <TableCell style={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>{getUserName(user)}</TableCell>
                        <TableCell style={{ fontFamily: FONT_FAMILY }}>{user.email || "-"}</TableCell>
                        <TableCell style={{ fontFamily: FONT_FAMILY }}>{getRoleLabel(getUserRole(user))}</TableCell>
                        <TableCell style={{ fontFamily: FONT_FAMILY }}>
                          <Chip size="small" color={getApprovalStatus(user) === "Approved" ? "success" : "warning"} label={getApprovalStatus(user)} />
                        </TableCell>
                        <TableCell style={{ fontFamily: FONT_FAMILY }}>
                          <Chip size="small" color={getActiveStatus(user) === "Active" ? "success" : "default"} label={getActiveStatus(user)} />
                        </TableCell>
                        <TableCell style={{ fontFamily: FONT_FAMILY }}>{getRelationshipAccessType(user, users, fleetStats)}</TableCell>
                        <TableCell style={{ fontFamily: FONT_FAMILY }}>
                          {canEditUsersModule && editRoute ? (
                            <Button size="small" variant="outlined" onClick={() => navigate(editRoute)}>Edit User</Button>
                          ) : (
                            <Chip size="small" label="View only" />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  }) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center" style={{ fontFamily: FONT_FAMILY, padding: 24 }}>No users found for this filter.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        <Paper style={{ borderRadius: 8, boxShadow: `0px 2px 5px ${SECONDORY_COLOR}`, padding: 20, backgroundColor: colors.WHITE }}>
          <Typography variant="h6" style={{ fontFamily: FONT_FAMILY, fontWeight: 700, marginBottom: 12 }}>4. Access Preview</Typography>
          <Typography variant="subtitle2" style={{ fontFamily: FONT_FAMILY, marginBottom: 8 }}>{getRoleLabel(selectedRole)}</Typography>
          <FormControl size="small" style={{ minWidth: 220, marginBottom: 12 }}>
            <Select value={selectedRole} onChange={(event) => setSelectedRole(event.target.value)}>
              {ROLE_DEFINITIONS.map((role) => <MenuItem key={role.id} value={role.id}>{role.label}</MenuItem>)}
            </Select>
          </FormControl>
          <Typography variant="body2" style={{ fontFamily: FONT_FAMILY, fontWeight: 700, color: MAIN_COLOR, marginBottom: 6 }}>Can Access</Typography>
          <Box style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
            {accessPreview.can.length ? accessPreview.can.map((item) => <Chip key={item} size="small" color="primary" label={item} />) : <Typography variant="body2">None</Typography>}
          </Box>
          <Typography variant="body2" style={{ fontFamily: FONT_FAMILY, fontWeight: 700, color: "#9b1c1c", marginBottom: 6 }}>Cannot Access</Typography>
          <Box style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {accessPreview.cannot.length ? accessPreview.cannot.map((item) => <Chip key={item} size="small" variant="outlined" label={item} />) : <Typography variant="body2">None</Typography>}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}



